import React from "react";
import { Flex, Text, Icon, AspectRatio } from "@chakra-ui/react";
import { useColorTheme } from "contexts/color-theme";
import { trackEvent } from "lib/analytics";
import { Link } from "ui/link";
import { DirectionalButton } from "ui/button";
import { DecorativeBackgroundImage } from "components/images";
import { type SubNavItemProps } from "./data";
import { SubNavTag } from "./sub-nav-tag";

type SubNavLinkProps = {
  /** Determines the hover background color of the link. */
  bgVariant?: "primary" | "secondary";
} & SubNavItemProps;

export function SubNavLink({
  bgVariant = "primary",
  target,
  label,
  subLabel,
  href,
  isArrowLink,
  tag,
  icon,
  image,
}: SubNavLinkProps) {
  const { theme, bgColor } = useColorTheme();

  const linkClicked = () => {
    trackEvent("Nav Item Clicked", {
      Label: label,
      Link: href,
    });
  };

  if (isArrowLink) {
    // Use the tertiary variant if the bgColor shade is .50, which only applies to light themes. Otherwise, use secondary.
    const variant = theme === "dark" || bgColor.includes("150") || bgColor === "white" ? "secondary" : "tertiary";

    return (
      <DirectionalButton href={href} variant={variant} mt="smaller" onClick={linkClicked}>
        {label}
      </DirectionalButton>
    );
  }

  const hoverBgColor = {
    primary: bgColor === "white" ? `brand.backgroundSecondary.light` : `brand.header.backgroundSecondary.${theme}`,
    secondary: bgColor,
  };

  return (
    <Link
      aria-label={label}
      href={href}
      target={target}
      className="group"
      variant="unstyled"
      borderRadius="base"
      bgColor="transparent"
      flexDir="column"
      alignItems="start"
      gap="smallest"
      p="smaller"
      width="100%"
      transition="background-color 0.15s ease-in-out"
      _hover={{
        bgColor: hoverBgColor[bgVariant],
        textDecoration: "none",
      }}
      onClick={linkClicked}
    >
      {image && (
        <AspectRatio
          ratio={1.9}
          w="100%"
          maxW={{ base: "500px", lg: "200px", xl: "100%" }}
          borderRadius="smaller"
          overflow="hidden"
          border="1px"
          borderColor={`brand.header.border.${theme}`}
        >
          <DecorativeBackgroundImage src={image} placeholder="blur" />
        </AspectRatio>
      )}

      <Flex align="center" gap="smallest">
        {icon && <Icon as={icon} boxSize="icon.md" />}
        <Flex flexDir="column" gap="tiny">
          <Flex align="center" gap="smallest">
            <Text as="span" textStyle="caption1" fontWeight="semibold" mt="tiniest">
              {label}
            </Text>

            {tag && <SubNavTag {...tag} />}
          </Flex>

          {subLabel && (
            <Text as="span" color={`brand.textTertiary.${theme}`} textStyle="caption2">
              {subLabel}
            </Text>
          )}
        </Flex>
      </Flex>
    </Link>
  );
}
